import './index.css';
import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import classnames from 'classnames';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { ComponentWithBadge } from '@hh.ru/magritte-ui-badge';
import { s as styles, I as IconDynamic } from './IconDynamic-D_LIh_94.js';
import { SkeletonSize16, SkeletonSize24, SkeletonSize32 } from './Skeleton.js';
import '@hh.ru/magritte-ui-skeleton';

const INCOMPLETE_SKELETON_BY_SIZE = {
    16: SkeletonSize16,
    24: SkeletonSize24,
    32: SkeletonSize32,
};
const ALLOWED_BADGE_SIZES = ['small', 'extra-small'];
const hoc = function (Icon, componentName) {
    const IconWrapperComponent = ({ initialColor: _initialColor, 'aria-label': ariaLabel, 'aria-hidden': ariaHidden, padding, borderRadius, borderStyle, backgroundStyle, shadow = 'level-0', increaseShadow, badge, onClick, disabled, hex, ...props }, ref) => {
        let highlightedValue;
        let disabledValue;
        let pressedValue;
        let hexHighlightedValue;
        let hexDisabledValue;
        let hexPressedValue;
        let dataQa;
        const initialColor = !_initialColor && !hex ? 'primary' : _initialColor;
        let propsToPass = { ...props };
        const withWrapper = !!padding ||
            !!borderRadius ||
            !!backgroundStyle ||
            !!borderStyle ||
            !!onClick ||
            shadow !== 'level-0' ||
            increaseShadow;
        const WrapperElement = onClick ? 'button' : 'span';
        const additionalProps = { ref };
        const isComponentDisabled = useDisabled() || !!disabled;
        if (WrapperElement === 'button') {
            additionalProps.type = 'button';
            additionalProps.disabled = isComponentDisabled;
            additionalProps.onClick = onClick;
        }
        if ('highlightedColor' in propsToPass) {
            const { highlightedColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            highlightedValue = highlightedColor;
        }
        if ('disabledColor' in propsToPass) {
            const { disabledColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            disabledValue = disabledColor;
        }
        if ('pressedColor' in propsToPass) {
            const { pressedColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            pressedValue = pressedColor;
        }
        if (highlightedValue && !pressedValue) {
            pressedValue = highlightedValue;
        }
        if ('hexHighlighted' in propsToPass) {
            const { hexHighlighted, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexHighlightedValue = hexHighlighted;
        }
        if ('hexDisabled' in propsToPass) {
            const { hexDisabled, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexDisabledValue = hexDisabled;
        }
        if ('hexPressed' in propsToPass) {
            const { hexPressed, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexPressedValue = hexPressed;
        }
        if (hexHighlightedValue && !hexPressedValue) {
            hexPressedValue = hexHighlightedValue;
        }
        if ('data-qa' in propsToPass) {
            const { 'data-qa': dataQaValue, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            dataQa = dataQaValue;
        }
        const icon = (jsx(Icon, { ...propsToPass, "aria-label": ariaLabel, "aria-hidden": ariaLabel ? ariaHidden || undefined : true, "data-qa": withWrapper && dataQa ? `${dataQa}-svg` : dataQa, role: "img", focusable: "false", ref: !withWrapper ? ref : undefined, className: classnames(styles.icon, {
                [styles[`icon_initial-${initialColor}`]]: !!initialColor,
                [styles[`icon_highlighted-${highlightedValue}`]]: !!highlightedValue,
                [styles[`icon_pressed-${pressedValue}`]]: !!pressedValue,
                [styles[`icon_disabled-${disabledValue}`]]: !!disabledValue,
                [styles.hexed]: hex || hexHighlightedValue || hexDisabledValue,
            }), style: {
                '--magritte-ui-icon-hex-color': hex,
                '--magritte-ui-icon-hex-highlighted-color': hexHighlightedValue,
                '--magritte-ui-icon-hex-pressed-color': hexPressedValue,
                '--magritte-ui-icon-hex-disabled-color': hexDisabledValue,
            } }));
        const wrappedIcon = badge && !backgroundStyle && shadow === 'level-0' && !isComponentDisabled ? (jsx(ComponentWithBadge, { allowedSizes: ALLOWED_BADGE_SIZES, badge: badge, offset: -3, children: icon })) : (icon);
        let wrapper = wrappedIcon;
        if (withWrapper) {
            const content = (jsx(WrapperElement, { ...additionalProps, "data-qa": dataQa, className: classnames(styles.view, {
                    [styles[`shadow-${shadow}`]]: shadow,
                    [styles.increase]: increaseShadow,
                }), style: { borderRadius: borderRadius === 'halfHeight' ? '9999px' : borderRadius }, children: jsx("span", { className: classnames(styles.view, backgroundStyle && styles[`style-${backgroundStyle}`], borderStyle && styles[`border-style-${borderStyle}`]), style: { borderRadius: borderRadius === 'halfHeight' ? '9999px' : borderRadius, padding }, children: wrappedIcon }) }));
            wrapper = (jsx("span", { className: classnames(styles.wrapper, {
                    [styles.active]: !!onClick,
                }), children: onClick ? jsx(IconDynamic, { children: content }) : content }));
        }
        if (badge && (backgroundStyle || shadow !== 'level-0') && !isComponentDisabled) {
            return (jsx(ComponentWithBadge, { allowedSizes: ALLOWED_BADGE_SIZES, badge: badge, offset: 0, children: wrapper }));
        }
        return wrapper;
    };
    const IconWrapper = forwardRef(IconWrapperComponent);
    const size = parseInt(componentName.slice(-2), 10);
    const IncompleteSkeleton = INCOMPLETE_SKELETON_BY_SIZE[size];
    const Skeleton = ((props) => (jsx(IncompleteSkeleton, { Icon: IconWrapper, ...props })));
    const IconWithSkeleton = IconWrapper;
    IconWithSkeleton.isIcon = true;
    IconWithSkeleton.Skeleton = Skeleton;
    IconWithSkeleton._size = size;
    return IconWithSkeleton;
};

export { hoc as default };
//# sourceMappingURL=hoc.js.map
